import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axios from "axios";
import ProductCard from "../components/Home/ProductCard";
import Loder from "../components/Loder";
import { Range } from "react-range";
import DeliveryInfo from "../components/Home/DeliveryInfo";

const Shop = () => {
  const baseUrl = process.env.REACT_APP_API_URL;
  const location = useLocation();

  // --- State Management ---
  const [values, setValues] = useState([0, 1500]);
  const [priceRangeFilter, setPriceRangeFilter] = useState("");
  const [sortOrder, setSortOrder] = useState(""); // NEW: State specifically for sorting
  const [isFilterVisible, setIsFilterVisible] = useState(false); // For mobile collapse
  const [categories, setCategories] = useState([]);
  // --- Category and Sub-category states ---
  const [activeCategory, setActiveCategory] = useState(null);
  const [subCategories, setSubCategories] = useState({});
  const [loadingSubCat, setLoadingSubCat] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}categories`
      );
      const data = await response.json();
      setCategories(data);
    };
    fetchCategories();
  }, []);

  const STEP = 1,
    MIN = 0,
    MAX = 1500;

  // --- Sub-Category Fetching Logic (Unchanged) ---
  const fetchSubCategories = async (categoryId) => {
    if (subCategories[categoryId]) return;
    setLoadingSubCat(categoryId);
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(
        `${baseUrl}sub-category/list?category_id=${categoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubCategories((prev) => ({
        ...prev,
        [categoryId]: res.data.data || [],
      }));
    } catch (error) {
      console.error("Failed to fetch sub-categories", error);
      toast.error("Failed to fetch sub-categories.");
      setSubCategories((prev) => ({ ...prev, [categoryId]: [] }));
    } finally {
      setLoadingSubCat(null);
    }
  };

  const handleIconToggle = (e, categoryId) => {
    e.stopPropagation();
    const newActiveCategory = activeCategory === categoryId ? null : categoryId;
    setActiveCategory(newActiveCategory);
    if (newActiveCategory) {
      fetchSubCategories(newActiveCategory);
    }
  };

  // --- Filter and Sort Handlers ---
  const handleFilterByPriceRange = () => {
    setPriceRangeFilter(`priceRange=${values[0]}-${values[1]}`);
  };

  // UPDATED: handleShort now only updates the sortOrder state for client-side sorting
  const handleShort = (e) => {
    setSortOrder(e.target.value);
  };

  // --- Data Fetching with React Query ---
  const searchParams = new URLSearchParams(location.search);
  const currentSubCategoryId = searchParams.get("subcategory");

  const fetchProducts = async () => {
    const token = localStorage.getItem("token");
    const filterQuery = [];

    if (priceRangeFilter) filterQuery.push(`filter=${priceRangeFilter}`);
    if (currentSubCategoryId)
      filterQuery.push(`subcategory=${currentSubCategoryId}`);

    const finalQuery =
      filterQuery.length > 0 ? `?${filterQuery.join("&")}` : "";
    try {
      const res = await axios.get(`${baseUrl}productlist${finalQuery}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (error) {
      console.error("Product Fetch Error:", error);
      toast.error("Failed to fetch products");
      throw new Error("Failed to fetch products");
    }
  };

  const { data: productsResponse, isLoading } = useQuery({
    queryKey: ["products-list", priceRangeFilter, location.search], // REMOVED dependency on sorting filter
    queryFn: fetchProducts,
    keepPreviousData: true,
  });

  const productsFromAPI = productsResponse?.products || [];
  const totalProducts = productsResponse?.totalCount || productsFromAPI.length;

  // --- Client-Side Sorting Logic ---
  const getFinalPrice = (product) => {
    const discountPrice = parseFloat(product.discount_price);
    // Use discount_price if it's a valid number greater than 0, otherwise use reguler_price
    return discountPrice > 0
      ? discountPrice
      : parseFloat(product.reguler_price);
  };

  const displayedProducts = useMemo(() => {
    let sortedProducts = [...productsFromAPI];
    if (sortOrder === "low-to-high") {
      sortedProducts.sort((a, b) => getFinalPrice(a) - getFinalPrice(b));
    } else if (sortOrder === "high-to-low") {
      sortedProducts.sort((a, b) => getFinalPrice(b) - getFinalPrice(a));
    }
    return sortedProducts;
  }, [productsFromAPI, sortOrder]);

  // --- Categories and Sub-Category Details Fetching ---
  const { data: categoriesArray = [], isLoading: categoriesLoading } = useQuery(
    {
      queryKey: ["categories-list-on-shop"],
      queryFn: async () => {
        const token = localStorage.getItem("token");
        try {
          const res = await fetch(`${baseUrl}catagorylist`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw new Error("Failed to fetch categories");
          const data = await res.json();
          return data.catagories || data.data || [];
        } catch (e) {
          console.error("Category Fetch Error:", e);
          toast.error("Failed to fetch categories");
          return [];
        }
      },
    }
  );

  const { data: subCategoryDetails } = useQuery({
    queryKey: ["subCategoryDetails", currentSubCategoryId],
    queryFn: async () => {
      if (!currentSubCategoryId) return null;
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(
          `${baseUrl}sub-category/details/${currentSubCategoryId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return res.data.data;
      } catch (error) {
        console.error("Failed to fetch sub-category details", error);
        return null;
      }
    },
    enabled: !!currentSubCategoryId,
  });

  const pageTitle = subCategoryDetails?.name || "Shop";
  const shopTitleBg = "#D93A3A",
    filterPanelBg = "#f0f2f5",
    filterTitleColor = "#D93A3A",
    filterButtonGradient = "linear-gradient(to right, #e5272e, #f59332)",
    primaryButtonTextColor = "#ffffff";

  return (
    <>
      <style>{`
        /* --- Styles for Sidebar Dropdown --- */
        .item-category { display: flex; flex-wrap: wrap; width: 100%; margin-bottom: 5px; }
        .category-item-row { flex-basis: 100%; display: flex; justify-content: space-between; align-items: center; }
        .category-name-link { flex-grow: 1; display: block; padding: 0.3rem 0; color: #333333; text-decoration: none; font-size: 0.8rem; font-weight: 600; transition: color 0.2s ease; }
        .category-name-link:hover { color: ${filterTitleColor}; }
        .toggle-icon { padding: 0.3rem 0.5rem; cursor: pointer; font-style: normal; font-weight: bold; color: #555; }
        .toggle-icon:hover { color: ${filterTitleColor}; }
        .sub-category-list { flex-basis: 100%; list-style: none; padding-left: 15px; max-height: 0; overflow: hidden; transition: max-height 0.4s ease-out; }
        .sub-category-list.open { max-height: 500px; transition: max-height 0.5s ease-in; }
        .sub-category-link { display: block; padding: 0.3rem 0; color: #555; text-decoration: none; font-size: 0.75rem; transition: color 0.2s ease; }
        .sub-category-link:hover { color: ${filterTitleColor}; }
        .sub-category-list .small-text { font-size: 0.75rem; color: #888; padding: 0.3rem 0; }
        /* --- Other Styles --- */
        .shop-page-main-container { }
        .shop-title-bar { background-color: ${shopTitleBg}; color: #ffffff; padding: 4rem 0; text-align: center; margin-bottom: 2.5rem; }
        .shop-title-bar h1 { font-size: 1.8rem; font-weight: bold; margin: 0; text-transform: uppercase; letter-spacing: 1px; }
        .filter-sidebar { background-color: ${filterPanelBg}; padding: 1.25rem; border-radius: 8px; position: sticky; top: 20px; height: fit-content; max-height: calc(100vh - 40px); overflow-y: auto; }
        .filter-sidebar h2, .filter-sidebar h3 { font-size: 0.85rem; font-weight: 600; color: ${filterTitleColor}; margin-top: 0; margin-bottom: 0.75rem; text-transform: uppercase; }
        .content-filter-price p { font-size: 0.85rem; color: #333; font-weight: 400; margin-bottom: 0.8rem; text-align: left; }
        .range-track { height: 5px !important; background: #B0BEC5 !important; border-radius: 3px; margin: 20px 5px 25px 5px !important; width: calc(100% - 10px) !important; position: relative; }
        .range-thumb { height: 18px !important; width: 18px !important; background-color: #0f4372 !important; border-radius: 50%; box-shadow: 0 1px 3px rgba(0,0,0,0.3); cursor: grab; }
        .filter-apply-btn { background-image: ${filterButtonGradient}; border: none; color: ${primaryButtonTextColor}; font-weight: 500; font-size: 0.85rem; padding: 0.5rem 1rem; width: 100%; margin-top: 1rem; border-radius: 6px; }
        .shop-toolbar { border-bottom: 1px solid #e0e0e0; margin-bottom: 1.5rem; }
        .product-item-col { display: flex; flex-direction: column; }
        /* --- Mobile Filter Toggle Button Style --- */
        .filter-toggle-btn {
            width: 100%;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
            background-image: ${filterButtonGradient};
            color: ${primaryButtonTextColor};
            border: none;
            padding: 0.6rem 1rem;
            border-radius: 8px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        /* --- Media Query for Mobile --- */
        @media (max-width: 767.98px) { 
          .filter-sidebar { 
            position: static; 
            max-height: none; 
            overflow-y: visible;
            background-color: transparent;
            padding: 0;
            border-radius: 0;
          } 
          .filter-sidebar-wrapper { margin-bottom: 1.5rem; }
          .main-Default-sorting { min-width: 100%; }
          /* Container for the collapsible content */
          .filter-collapse-container.show {
            background-color: ${filterPanelBg};
            padding: 1.25rem;
            border-radius: 8px;
            margin-top: 1rem;
          }
        }
      `}</style>

      <div className="shop-page-main-container">
        <section className="shop-title-bar">
          <div className="container">
            <h1>{pageTitle.toUpperCase()}</h1>
          </div>
        </section>

        <section className="mainsec-5 py-4 container">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-3 col-md-4 filter-sidebar-wrapper">
                {/* Mobile Filter Toggle Button */}
                <button
                  className="btn filter-toggle-btn d-md-none"
                  type="button"
                  onClick={() => setIsFilterVisible(!isFilterVisible)}
                  aria-expanded={isFilterVisible}
                  aria-controls="filterCollapse"
                >
                  <span>Filters</span>
                  <span>{isFilterVisible ? "−" : "+"}</span>
                </button>

                {/* Collapsible Filter Area: FIX for closing issue */}
                <div
                  className={`filter-collapse-container collapse d-md-block ${
                    isFilterVisible ? "show" : ""
                  }`}
                  id="filterCollapse"
                >
                  <aside className="filter filter-sidebar">
                    {/* Categories Section */}
                    <div className="filter-section">
                      <h2>Categories</h2>
                      <div id="pa_category" className="filter_category_product">
                        {categoriesLoading ? (
                          <small className="text-muted d-block">
                            Loading...
                          </small>
                        ) : categoriesArray && categoriesArray.length > 0 ? (
                          categoriesArray.map((category) => (
                            <div key={category._id} className="item-category">
                              <div className="category-item-row">
                                <Link
                                  to={`/category/${category._id}`}
                                  className="category-name-link"
                                >
                                  {category?.catagoryname}
                                </Link>
                                <i
                                  className="toggle-icon"
                                  onClick={(e) =>
                                    handleIconToggle(e, category._id)
                                  }
                                >
                                  {activeCategory === category._id ? "−" : "+"}
                                </i>
                              </div>
                              <ul
                                className={`sub-category-list ${
                                  activeCategory === category._id ? "open" : ""
                                }`}
                              >
                                {loadingSubCat === category._id ? (
                                  <li>
                                    <small className="small-text">
                                      Loading...
                                    </small>
                                  </li>
                                ) : subCategories[category._id]?.length > 0 ? (
                                  subCategories[category._id].map((sub) => (
                                    <li key={sub._id}>
                                      <Link
                                        to={`/shop?subcategory=${sub._id}`}
                                        className="sub-category-link"
                                      >
                                        {sub.name}
                                      </Link>
                                    </li>
                                  ))
                                ) : (
                                  !loadingSubCat &&
                                  subCategories[category._id] && (
                                    <li>
                                      <small className="small-text">
                                        No sub-categories
                                      </small>
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          ))
                        ) : (
                          <small className="text-muted d-block">
                            No categories.
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Price Filter Section */}
                    <div className="filter-section mt-4">
                      <h3>Price</h3>
                      <div className="content-filter-price">
                        <p className="price-range-display">
                          Price Range: £{values[0]} - £{values[1]}
                        </p>
                        <Range
                          values={values}
                          step={STEP}
                          min={MIN}
                          max={MAX}
                          onChange={(vals) => setValues(vals)}
                          renderTrack={({ props, children }) => (
                            <div
                              {...props.style}
                              onMouseDown={props.onMouseDown}
                              onTouchStart={props.onTouchStart}
                              style={{
                                ...props.style,
                                display: "flex",
                                width: "100%",
                              }}
                            >
                              <div ref={props.ref} className="range-track">
                                {children}
                              </div>
                            </div>
                          )}
                          renderThumb={({ props }) => (
                            <div {...props} className="range-thumb" />
                          )}
                        />
                        <button
                          onClick={handleFilterByPriceRange}
                          type="button"
                          className="btn filter-apply-btn"
                        >
                          Filter
                        </button>
                      </div>
                    </div>
                  </aside>
                </div>
              </div>

              <div className="col-lg-9 col-md-8">
                <div className="shop-toolbar d-md-flex align-items-center justify-content-md-between mb-4">
                  <div className="Showing mb-2 mb-md-0">
                    Showing {displayedProducts.length} of {totalProducts}{" "}
                    results
                  </div>
                  <div className="main-Default-sorting">
                    {/* UPDATED: This select now controls the client-side sortOrder state */}
                    <select
                      style={{ minWidth: "200px" }}
                      className="form-select shop-sort-select"
                      value={sortOrder}
                      onChange={handleShort}
                    >
                      <option value="">Default sorting</option>
                      <option value="low-to-high">Price : Low to High</option>
                      <option value="high-to-low">Price : High to Low</option>
                    </select>
                  </div>
                </div>
                {isLoading ? (
                  <div className="text-center py-5">
                    <Loder />
                  </div>
                ) : (
                  <div className="row g-3 product-grid-row">
                    {displayedProducts.length > 0 ? (
                      displayedProducts.map((item) => (
                        <div
                          className="col-6 col-md-4 product-item-col mb-4"
                          key={item._id}
                        >
                          <ProductCard item={item} categories={categories} />
                        </div>
                      ))
                    ) : (
                      <div className="col-12 text-center py-5">
                        <p className="no-products-message">
                          No products found.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
      <DeliveryInfo />
    </>
  );
};

export default Shop;
